<template>
  <!-- 원문(paper.md) 경량 렌더러 — 의존성 없이 제목·문단·표·목록·굵게·기울임·코드·링크·인용만 처리한다(2026-09-27 결정 1).
       highlight 로 넘어온 문장은 <mark> 로 감싸고 스크롤한다(근거 문장 → 원문 연결). -->
  <div class="lit-md" v-html="html" ref="root"></div>
</template>

<script>
function escapeHtml(s) {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

const HL_OPEN = "\u0001";
const HL_CLOSE = "\u0002";

/** 공백·<sup> 등 표기 차이를 무시하고 quote 를 md 원문에서 찾아 마커로 감싼다 */
function markHighlight(md, quote) {
  if (!quote || quote.trim().length < 8) return md;
  const norm = (s) => s.replace(/<[^>]+>/g, "").replace(/[\s ]+/g, " ").trim();
  const q = norm(quote);
  // 정규식: 공백은 \s+ 로, 나머지 문자는 이스케이프
  const tokens = q.split(" ").map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  const re = new RegExp(tokens.join("[\\s\\u00a0]+"), "i");
  const m = re.exec(md);
  if (!m) {
    // 앞 60자만으로 다시 시도(표 셀 구분자 등으로 뒤가 깨진 경우)
    const shortTokens = tokens.slice(0, Math.max(3, Math.min(tokens.length, 8)));
    const re2 = new RegExp(shortTokens.join("[\\s\\u00a0]+"), "i");
    const m2 = re2.exec(md);
    if (!m2) return md;
    return md.slice(0, m2.index) + HL_OPEN + m2[0] + HL_CLOSE + md.slice(m2.index + m2[0].length);
  }
  return md.slice(0, m.index) + HL_OPEN + m[0] + HL_CLOSE + md.slice(m.index + m[0].length);
}

function inline(s) {
  let out = escapeHtml(s);
  out = out.replace(/`([^`]+)`/g, "<code>$1</code>");
  out = out.replace(/\*\*([^*]+)\*\*/g, "<b>$1</b>");
  out = out.replace(/(^|[^*])\*([^*\n]+)\*/g, "$1<i>$2</i>");
  out = out.replace(/\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/g, '<a href="$2" target="_blank" rel="noopener">$1</a>');
  out = out.replace(/(^|\s)(https?:\/\/[^\s<]+)/g, '$1<a href="$2" target="_blank" rel="noopener">$2</a>');
  out = out.split(HL_OPEN).join('<mark class="lit-hl">').split(HL_CLOSE).join("</mark>");
  return out;
}

function render(md) {
  const lines = md.replace(/\r\n?/g, "\n").split("\n");
  const html = [];
  let i = 0;
  let para = [];
  const flushPara = () => {
    if (para.length) { html.push("<p>" + inline(para.join(" ")) + "</p>"); para = []; }
  };
  while (i < lines.length) {
    const line = lines[i];
    const t = line.trim();
    if (t.startsWith("```")) {
      flushPara();
      const buf = [];
      i += 1;
      while (i < lines.length && !lines[i].trim().startsWith("```")) { buf.push(lines[i]); i += 1; }
      html.push("<pre><code>" + escapeHtml(buf.join("\n")) + "</code></pre>");
      i += 1;
      continue;
    }
    const h = /^(#{1,6})\s+(.*)$/.exec(t);
    if (h) {
      flushPara();
      const level = h[1].length;
      html.push(`<h${level}>${inline(h[2])}</h${level}>`);
      i += 1;
      continue;
    }
    if (t.startsWith("|") && i + 1 < lines.length && /^\|?\s*:?-{2,}/.test(lines[i + 1].trim())) {
      flushPara();
      const cells = (row) => row.trim().replace(/^\||\|$/g, "").split("|").map((c) => inline(c.trim()));
      const head = cells(t);
      i += 2;
      const body = [];
      while (i < lines.length && lines[i].trim().startsWith("|")) { body.push(cells(lines[i])); i += 1; }
      html.push("<table><thead><tr>" + head.map((c) => `<th>${c}</th>`).join("") + "</tr></thead><tbody>" +
        body.map((r) => "<tr>" + r.map((c) => `<td>${c}</td>`).join("") + "</tr>").join("") + "</tbody></table>");
      continue;
    }
    if (/^[-*]\s+/.test(t) || /^\d+[.)]\s+/.test(t)) {
      flushPara();
      const ordered = /^\d+[.)]\s+/.test(t);
      const items = [];
      while (i < lines.length && (/^[-*]\s+/.test(lines[i].trim()) || /^\d+[.)]\s+/.test(lines[i].trim()))) {
        items.push(inline(lines[i].trim().replace(/^([-*]|\d+[.)])\s+/, "")));
        i += 1;
      }
      html.push((ordered ? "<ol>" : "<ul>") + items.map((x) => `<li>${x}</li>`).join("") + (ordered ? "</ol>" : "</ul>"));
      continue;
    }
    if (t.startsWith(">")) {
      flushPara();
      const buf = [];
      while (i < lines.length && lines[i].trim().startsWith(">")) { buf.push(lines[i].trim().replace(/^>\s?/, "")); i += 1; }
      html.push("<blockquote>" + inline(buf.join(" ")) + "</blockquote>");
      continue;
    }
    if (t === "") { flushPara(); i += 1; continue; }
    if (/^(-{3,}|\*{3,})$/.test(t)) { flushPara(); html.push("<hr>"); i += 1; continue; }
    para.push(t);
    i += 1;
  }
  flushPara();
  return html.join("\n");
}

export default {
  name: "LitMarkdown",
  props: {
    source: { type: String, default: "" },
    highlight: { type: String, default: "" },
  },
  computed: {
    html() {
      return render(markHighlight(this.source || "", this.highlight || ""));
    },
    found() {
      return this.html.indexOf('class="lit-hl"') >= 0;
    },
  },
  watch: {
    html() {
      this.$nextTick(this.scrollToMark);
    },
  },
  mounted() {
    this.$nextTick(this.scrollToMark);
  },
  methods: {
    scrollToMark() {
      const el = this.$refs.root && this.$refs.root.querySelector("mark.lit-hl");
      if (el) el.scrollIntoView({ block: "center", behavior: "smooth" });
      this.$emit("highlight-result", !!el);
    },
  },
};
</script>

<style>
.lit-md { font-size: 14px; line-height: 1.75; color: #223; word-break: break-word; }
.lit-md h1 { font-size: 20px; margin: 18px 0 10px; }
.lit-md h2 { font-size: 17px; margin: 22px 0 8px; border-bottom: 1px solid #e3e8ee; padding-bottom: 4px; }
.lit-md h3 { font-size: 15px; margin: 16px 0 6px; }
.lit-md h4, .lit-md h5, .lit-md h6 { font-size: 14px; margin: 12px 0 4px; }
.lit-md p { margin: 0 0 10px; }
.lit-md table { border-collapse: collapse; margin: 10px 0 14px; font-size: 12.5px; display: block; overflow-x: auto; max-width: 100%; }
.lit-md th, .lit-md td { border: 1px solid #dde3ea; padding: 4px 8px; text-align: left; vertical-align: top; }
.lit-md th { background: #f4f7fa; }
.lit-md code { background: #f4f7fa; padding: 1px 5px; border-radius: 3px; font-family: Consolas, "Courier New", monospace; font-size: 12.5px; }
.lit-md pre { background: #1e2a36; color: #e6edf3; padding: 10px 12px; border-radius: 6px; overflow-x: auto; }
.lit-md pre code { background: none; color: inherit; padding: 0; }
.lit-md blockquote { border-left: 3px solid #cfd8e3; margin: 8px 0; padding: 4px 12px; color: #556; }
.lit-md ul, .lit-md ol { padding-left: 22px; margin: 0 0 10px; }
.lit-md a { color: #0c78b3; }
.lit-md mark.lit-hl { background: #ffe58a; padding: 1px 2px; border-radius: 2px; }
</style>
